import { getI18n, useTranslation } from "react-i18next";
import { CardSpg, iTxt } from "@duosoftbg/nacid-components";
import { useState, useEffect } from "react";
import React from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { Tooltip } from "react-tooltip";
import { Box } from "@mui/system";
import { countryCodeToName, countryCodeToNameBG, countryNameToCode } from "./mapOfTheWorldData";
import { EQF_STATUS_COLORS } from "./CountryCardsPage";
import { tooltipTextStyles, countryColors, getGeographyStyle, getTooltipStyle } from "./stylesMapOfTheWord";
import bookIcon from "./icons/icons8-book-50.png";
import { CountryDetails } from "../../../types/QFDataTypes";
import DialogQF from "./DialogQF";

export const MapOfTheWorld = ({ active, geoData }: { active: CountryDetails[]; geoData?: unknown }) => {
  const { t } = useTranslation();
  const [tooltipContent, setTooltipContent] = useState("");
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [countryId, setCountryId] = useState(null);
  const [zoom] = useState(1);
  const minZoom = 0.7;
  const maxZoom = 5;
  const widthMap = 500;
  const heightMap = 650;
  const translateExtentCoordinates: [[number, number], [number, number]] = [
    [-220, -155],
    [710, 775],
  ];

  const activeCountries = (active ?? []).map((item) => countryCodeToName[item?.country?.id]);

  useEffect(() => {
    const handleMouseMove = (e: { clientX: any; clientY: any }) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleLeftClick = (geo: any) => {
    const countryName = geo.properties.name;
    if (activeCountries.includes(countryName)) {
      setCountryId(countryNameToCode[countryName]);
    }
  };

  const handleCloseDialog = () => {
    setCountryId(null);
  };

  return (
    <Box style={{ height: "100%", border: "0px", borderRadius: "10px" }}>
      <Box fontWeight={200} fontSize={24} style={{ textAlign: "center" }}>
        {t("t.qualCompare.mapTitle")}
      </Box>
      <div style={{ width: "100%", height: "100%", paddingTop: "20px" }}>
        <CardSpg style={{ border: "1px solid #656D78", borderRadius: "10px", position: "relative" }}>
          <div style={{ width: "100%", height: "100%" }}>
            <ComposableMap projection="geoMercator" width={widthMap} height={heightMap}>
              <ZoomableGroup
                zoom={zoom}
                minZoom={minZoom}
                maxZoom={maxZoom}
                translateExtent={translateExtentCoordinates}
              >
                <Geographies geography={geoData}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const countryName = geo.properties.name;
                      const countryFill = activeCountries.includes(countryName)
                        ? countryColors.defaultActiveCountryColor
                        : countryColors.defaultCountryColor;
                      const countryHover = activeCountries.includes(countryName)
                        ? countryColors.hoverActiveCountryColor
                        : countryColors.hoverCountryColor;
                      const countryPressed = activeCountries.includes(countryName)
                        ? countryColors.pressedActiveCountryColor
                        : countryColors.pressedCountryColor;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          data-tip=""
                          data-tooltip-html={
                            activeCountries.includes(countryName)
                              ? getTooltipHtml(active.find((r) => r.country.id === countryNameToCode[countryName]))
                              : getEmptyTooltipHtml(countryName)
                          }
                          onMouseEnter={() => setTooltipContent(countryName)}
                          onMouseLeave={() => setTooltipContent("")}
                          onClick={() => handleLeftClick(geo)}
                          style={getGeographyStyle(countryName, activeCountries, {
                            fill: countryFill,
                            hover: countryHover,
                            pressed: countryPressed,
                          })}
                          data-tooltip-id="world-tooltip"
                          data-tooltip-content={tooltipContent}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
          <Tooltip id="world-tooltip" style={getTooltipStyle(mousePosition)} />
        </CardSpg>
        {countryId && <DialogQF countryId={countryId} onClose={() => handleCloseDialog()} />}
      </div>
    </Box>
  );

  function getTooltipHtml(countryDetails: CountryDetails) {
    const flagUrl = countryDetails ? `https://flagcdn.com/w40/${countryDetails.country.id.toLowerCase()}.png` : null;
    const dotColor = countryDetails ? EQF_STATUS_COLORS[countryDetails.eqfStatus] : null;
    return `
     <div style="${tooltipTextStyles.container}">
        <div style="${tooltipTextStyles.header}">
            <img src="${flagUrl}" alt="-" style="${tooltipTextStyles.flagImg}" />
            <h3 style="${tooltipTextStyles.title}">
              ${iTxt(getI18n().language, countryDetails.country.name, countryDetails.country.nameEn)}
            </h3>
        </div>
        <div style="${tooltipTextStyles.divider}"></div>
        <div style="${tooltipTextStyles.paragraph}">${iTxt(getI18n().language, countryDetails.systemSummary, countryDetails.systemSummaryEn)}></div>
        <div style="${tooltipTextStyles.divider}"></div>
        <div style="${tooltipTextStyles.boldCenter}">
           <span style="${tooltipTextStyles.eqfDot(dotColor)}">●</span>
           <span style="margin-left: 4px;">${t(`l.eqfStatus.${countryDetails.eqfStatus}`)}</span>
        </div>
        <div style="${tooltipTextStyles.divider}"></div>
        <div style="${tooltipTextStyles.typeRow}">
           <span>${t(`l.systemType`)}: </span>
           <span style="${tooltipTextStyles.boldText}">${iTxt(getI18n().language, countryDetails.systemType.name, countryDetails.systemType.nameEn)}</span>
        </div>   
        <div style="${tooltipTextStyles.divider}"></div>
        <div style="${tooltipTextStyles.paragraph}">
            ${iTxt(getI18n().language, countryDetails.eqfDescription, countryDetails.eqfDescriptionEn)}
         </div>
     </div>
    `;
  }

  function getEmptyTooltipHtml(countryName: string) {
    const countryCode: string = countryNameToCode[countryName];
    const flagUrl = countryCode?.match(/^[0-9]$/)
      ? bookIcon
      : `https://flagcdn.com/w40/${countryCode?.toLowerCase()}.png`;
    return `<div style="${tooltipTextStyles.container}">
                <div style="text-align: center">
                    <img
                        src=${flagUrl}
                        style={{ maxWidth: "10px", marginTop: "10px" }}
                        alt="-"/>
                    <h3>${iTxt(getI18n().language, countryCodeToNameBG[countryCode], countryCodeToName[countryCode])}</h3>
                </div>
             <div/>`;
  }
};
